import type {
  EnvironmentsSchemaCampaign,
  EnvironmentsSchemaDesignStudioAsset,
  EnvironmentsSchemaLayout,
  EnvironmentsSchemaNewsletter,
  EnvironmentsSchemaSegment,
  EnvironmentsSchemaSnippet,
  EnvironmentsSchemaTemplate,
} from '@customerio/hydra-ui-api';
import { generatePath } from '@/services/router/generate-path';

/*
 * `generatePath` rather than `router.buildLocation`, which these tree-owned
 * routes would otherwise prefer: this module is a plain one, with no router to
 * read, and none of these hrefs carries a search param — the case where
 * `buildLocation` would quote the value.
 */

/**
 * Where each thing that uses an item lives.
 *
 * Every one of these opens in a new tab: the point of the usage tab is to
 * go and look at what is in the way, and losing the item's own page while
 * doing it would mean finding it again afterwards.
 */

export function campaignHref(workspaceId: number, campaignId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
    workspaceId: String(workspaceId),
    campaignId: String(campaignId),
  });
}

export function segmentHref(workspaceId: number, segmentId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', {
    workspaceId: String(workspaceId),
    segmentId: String(segmentId),
  });
}

export function newsletterHref(workspaceId: number, newsletterId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId/overview/recipients', {
    workspaceId: String(workspaceId),
    newsletterId: String(newsletterId),
  });
}

export function templateHref(workspaceId: number, templateId: number): string {
  return generatePath('/workspaces/:workspaceId/template/:templateId', {
    workspaceId: String(workspaceId),
    templateId: String(templateId),
  });
}

export function layoutHref(workspaceId: number, layoutId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/layouts/:layoutId', {
    workspaceId: String(workspaceId),
    layoutId: String(layoutId),
  });
}

export function snippetHref(workspaceId: number, snippetId: number): string {
  return generatePath('/workspaces/:workspaceId/snippets/:snippetId', {
    workspaceId: String(workspaceId),
    snippetId: String(snippetId),
  });
}

/**
 * A Design Studio asset's editor.
 *
 * The generic editor route takes any node id, so every asset links whatever
 * its type — a type this page has not seen is still reachable, which is what
 * the page it replaces does. WhatsApp is the one type with an editor of its
 * own; the page this replaces leaves it to the generic route and lets that
 * work out what it is holding.
 */
export function designStudioHref(workspaceId: number, type: string | null | undefined, id: string): string {
  // Encoded: path substitution writes a value as it is, and the id comes off
  // the wire.
  const assetId = encodeURIComponent(id);

  if (type === 'WHATSAPP') {
    return generatePath('/workspaces/:workspaceId/design-studio/editor/whatsapp/:id', {
      workspaceId: String(workspaceId),
      id: assetId,
    });
  }
  return generatePath('/workspaces/:workspaceId/design-studio/editor/:id', {
    workspaceId: String(workspaceId),
    id: assetId,
  });
}

/**
 * One row from any usage surface. Each surface's records carry their own id
 * field and nothing else in common, so the union is what the detail read
 * returns rather than a shape this page invents.
 */
export type UsageRecord =
  | EnvironmentsSchemaCampaign
  | EnvironmentsSchemaSegment
  | EnvironmentsSchemaNewsletter
  | EnvironmentsSchemaTemplate
  | EnvironmentsSchemaSnippet
  | EnvironmentsSchemaLayout
  | EnvironmentsSchemaDesignStudioAsset;

/**
 * What tells a record apart from its siblings, and where it opens.
 *
 * Both answers come from the same question — which id field the record
 * carries — so they are read together. The id matters because two records on
 * one surface can share a name, which makes the name no key at all.
 */
export type UsageTarget = { id: string; href: string | null };

export function usageTarget(workspaceId: number, record: UsageRecord): UsageTarget {
  if ('segment_id' in record) {
    return { id: `segment:${record.segment_id}`, href: segmentHref(workspaceId, record.segment_id) };
  }
  if ('campaign_id' in record) {
    return { id: `campaign:${record.campaign_id}`, href: campaignHref(workspaceId, record.campaign_id) };
  }
  if ('newsletter_id' in record) {
    return { id: `newsletter:${record.newsletter_id}`, href: newsletterHref(workspaceId, record.newsletter_id) };
  }
  if ('template_id' in record) {
    return { id: `template:${record.template_id}`, href: templateHref(workspaceId, record.template_id) };
  }
  if ('layout_id' in record) {
    return { id: `layout:${record.layout_id}`, href: layoutHref(workspaceId, record.layout_id) };
  }
  if ('short_id' in record) {
    return { id: `design-studio:${record.short_id}`, href: designStudioHref(workspaceId, record.type, record.id) };
  }
  if (typeof record.id === 'number') {
    return { id: `snippet:${record.id}`, href: snippetHref(workspaceId, record.id) };
  }
  // No id to link by. The name is all there is to key on, and nowhere to go.
  return { id: record.name, href: null };
}
